import "./styles.css";
import {
  filterAndSortImages,
  formatBytes,
  formatDate,
  getLatestStable,
  getMajorVersions,
  validateResourceCatalog,
  validateResourceIndex,
} from "./catalog";
import type { ImageEntry, Platform, ResourceCatalog, ResourceIndex, SortOption } from "./types";

const PAGE_SIZE = 20;
const DOCUMENTATION_URL = "https://docs.gitlab.cn/docs/jh/install/docker/";
const JIHU_URL = "https://gitlab.cn";
const appElement = document.querySelector<HTMLDivElement>("#app");

if (!appElement) throw new Error("#app element is required");
const app: HTMLDivElement = appElement;

let catalog: ResourceCatalog;
let query = "";
let versionFilter = "";
let sort: SortOption = "version-desc";
let visibleCount = PAGE_SIZE;
const expandedCards = new Set<string>();
let routeVersion = 0;

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function platformLabel(platform: Platform): string {
  return `${platform.os}/${platform.architecture}${platform.variant ? `/${platform.variant}` : ""}`;
}

function siteHeader(): string {
  return `
    <header class="site-header">
      <div class="site-header__inner content-width">
        <a class="brand" href="/" data-route aria-label="极狐GitLab 镜像资源首页">
          <img src="/logo.svg" alt="" />
        </a>
        <nav class="site-nav" aria-label="站点导航">
          <a href="${DOCUMENTATION_URL}" target="_blank" rel="noreferrer">文档中心</a>
          <a href="${JIHU_URL}" target="_blank" rel="noreferrer">极狐官网</a>
        </nav>
      </div>
    </header>`;
}

function siteFooter(): string {
  return `
    <footer>
      <div class="footer__inner content-width">
        <p>©${new Date().getFullYear()} 极狐信息技术（湖北）有限公司版权所有</p>
      </div>
    </footer>`;
}

function renderSiteShell(): void {
  app.innerHTML = `
    <div class="site-shell">
      ${siteHeader()}
      <main id="main-content"></main>
      ${siteFooter()}
      <div id="copy-status" class="sr-only" aria-live="polite"></div>
    </div>`;
}

async function fetchJson(path: string, label: string): Promise<unknown> {
  const response = await fetch(path, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`${label}请求失败（HTTP ${response.status}）。`);
  return response.json();
}

function syncResourceTitleTooltips(): void {
  for (const title of document.querySelectorAll<HTMLElement>(".resource-card__title")) {
    const isTruncated =
      title.scrollWidth > title.clientWidth + 1 || title.scrollHeight > title.clientHeight + 1;
    if (isTruncated) title.title = title.textContent?.trim() ?? "";
    else title.removeAttribute("title");
  }
}

function renderResourceCards(index: ResourceIndex, searchQuery: string): void {
  const grid = document.querySelector<HTMLDivElement>("#resource-grid");
  const status = document.querySelector<HTMLElement>("#resource-result-count");
  if (!grid || !status) throw new Error("资源列表容器不完整。");
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase("zh-CN");
  const resources = index.resources.filter((resource) =>
    [resource.name, resource.description, resource.repository]
      .join(" ")
      .toLocaleLowerCase("zh-CN")
      .includes(normalizedQuery),
  );
  status.textContent = `找到 ${resources.length} 个资源`;
  grid.innerHTML =
    resources.length > 0
      ? resources
          .map(
            (resource) => `
              <a class="resource-card" href="/${encodeURIComponent(resource.slug)}" data-route data-resource-id="${escapeHtml(resource.id)}">
                <h2 class="resource-card__title">${escapeHtml(resource.name)}</h2>
                <div class="resource-card__details">
                  <p class="resource-card__description">${escapeHtml(resource.description)}</p>
                  <p class="resource-card__date">最新发布于：${escapeHtml(formatDate(resource.latestPushedAt))}</p>
                </div>
              </a>`,
          )
          .join("")
      : `
        <div class="resource-empty">
          <h2>没有找到匹配资源</h2>
          <p>请尝试搜索资源名称、描述或 repository。</p>
        </div>`;
  requestAnimationFrame(syncResourceTitleTooltips);
}

function renderResourceIndex(index: ResourceIndex): void {
  document.title = "极狐GitLab 镜像资源";
  const shell = document.querySelector<HTMLElement>(".site-shell");
  const main = document.querySelector<HTMLElement>("#main-content");
  if (!shell || !main) throw new Error("页面骨架不完整。");
  shell.className = "site-shell resource-index-page";
  main.className = "resource-index content-width";
  main.innerHTML = `
    <div class="resource-index__heading">
      <h1>极狐GitLab 镜像资源</h1>
      <label class="resource-search">
        <span class="sr-only">搜索资源</span>
        <img src="/assets/search.svg" alt="" />
        <input id="resource-search" type="search" placeholder="搜索资源" autocomplete="off" aria-describedby="resource-result-count" />
        <button id="resource-search-clear" class="search-clear" type="button" aria-label="清除资源搜索" hidden><span aria-hidden="true">×</span></button>
      </label>
    </div>
    <p id="resource-result-count" class="sr-only" aria-live="polite"></p>
    <div id="resource-grid" class="resource-grid"></div>`;
  const searchInput = document.querySelector<HTMLInputElement>("#resource-search");
  const searchClear = document.querySelector<HTMLButtonElement>("#resource-search-clear");
  searchInput?.addEventListener("input", () => {
    if (searchClear) searchClear.hidden = searchInput.value.length === 0;
    renderResourceCards(index, searchInput.value);
  });
  searchClear?.addEventListener("click", () => {
    if (!searchInput) return;
    searchInput.value = "";
    searchClear.hidden = true;
    renderResourceCards(index, "");
    searchInput.focus();
  });
  renderResourceCards(index, "");
}

function archChips(image: ImageEntry, modifier = ""): string {
  if (image.detailsStatus !== "available" || image.platforms.length === 0) {
    return '<span class="arch-chip arch-chip--muted">平台详情不可用</span>';
  }
  return image.platforms
    .map((platform) => {
      const label = platformLabel(platform);
      return `<span class="arch-chip${modifier}" title="${escapeHtml(label)} · ${escapeHtml(
        formatBytes(platform.size),
      )}">${escapeHtml(label)}</span>`;
    })
    .join("");
}

function commandButton(command: string, label: string, compact = false): string {
  const modifier = compact ? " command--compact" : "";
  const copyAsset = compact ? "/assets/copy-small.svg" : "/assets/copy.svg";
  return `
    <button class="command${modifier}" type="button" data-copy="${escapeHtml(command)}" aria-label="${escapeHtml(label)}">
      <code>${escapeHtml(command)}</code>
      <span class="command__icon" aria-hidden="true">
        <img class="command__copy" src="${copyAsset}" alt="" />
        <img class="command__success" src="/assets/check.svg" alt="" />
      </span>
    </button>`;
}

function digestRows(image: ImageEntry): string {
  if (image.detailsStatus !== "available" || image.platforms.length === 0) {
    return '<div class="platform-unavailable">该历史镜像未返回平台详情，无法提供平台级固定 Digest。</div>';
  }
  return image.platforms
    .map((platform) => {
      const label = platformLabel(platform);
      const command = `docker pull ${catalog.resource.repository}@${platform.digest}`;
      return `
        <div class="digest-row">
          <span class="digest-row__platform">${escapeHtml(label)}</span>
          ${commandButton(command, `复制 ${image.canonicalTag} 的 ${label} 固定 Digest`, true)}
        </div>`;
    })
    .join("");
}

function imageCard(image: ImageEntry): string {
  const isExpanded = expandedCards.has(image.canonicalTag);
  const detailsId = `details-${image.canonicalTag.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`;
  return `
    <article class="image-card${isExpanded ? " image-card--expanded" : ""}" data-image-card="${escapeHtml(image.canonicalTag)}">
      <button
        class="image-card__summary"
        type="button"
        data-card-toggle="${escapeHtml(image.canonicalTag)}"
        aria-expanded="${String(isExpanded)}"
        aria-controls="${detailsId}"
        aria-label="${isExpanded ? "收起" : "展开"} ${escapeHtml(image.canonicalTag)} 镜像详情"
      >
        <span class="image-card__identity">
          <span class="image-card__title" role="heading" aria-level="3">${escapeHtml(image.canonicalTag)}</span>
          <span class="image-card__platforms">${archChips(image)}</span>
        </span>
        <span class="image-card__aside">
          <span class="image-card__date">发布于：${escapeHtml(formatDate(image.pushedAt))}</span>
          <img class="image-card__chevron" src="/assets/chevron-down.svg" alt="" />
        </span>
      </button>
      <div id="${detailsId}" class="image-card__details" aria-hidden="${String(!isExpanded)}" ${isExpanded ? "" : "inert"}>
        <div class="image-card__details-clip">
          <div class="image-card__details-content">
            ${commandButton(image.pullCommand, `复制 ${image.canonicalTag} 拉取命令`)}
            <h4>固定版本 Digest</h4>
            <div class="digest-list">${digestRows(image)}</div>
          </div>
        </div>
      </div>
    </article>`;
}

function renderResults(): void {
  const list = document.querySelector<HTMLDivElement>("#image-list");
  const count = document.querySelector<HTMLElement>("#result-count");
  const loadMore = document.querySelector<HTMLButtonElement>("#load-more");
  if (!list || !count || !loadMore) throw new Error("镜像列表容器不完整。");
  const results = filterAndSortImages([...catalog.images], query, versionFilter, sort);
  const visible = results.slice(0, visibleCount);
  count.textContent = `找到 ${results.length} 个镜像版本`;
  list.innerHTML =
    visible.length > 0
      ? visible.map(imageCard).join("")
      : '<div class="empty-state"><h3>没有找到匹配版本</h3><p>可搜索版本号、标签、架构或 Digest，也可以调整版本筛选。</p></div>';
  loadMore.hidden = visible.length >= results.length;
}

function updateSearchClearButton(searchInput: HTMLInputElement): void {
  const clearButton = document.querySelector<HTMLButtonElement>("#search-clear");
  if (clearButton) clearButton.hidden = searchInput.value.length === 0;
}

function clearSearch(searchInput: HTMLInputElement): void {
  searchInput.value = "";
  query = "";
  visibleCount = PAGE_SIZE;
  updateSearchClearButton(searchInput);
  renderResults();
}

async function copyCommand(button: HTMLButtonElement): Promise<void> {
  const command = button.dataset.copy;
  if (!command) throw new Error("复制按钮缺少命令。");
  const originalLabel = button.getAttribute("aria-label") ?? "复制命令";
  const liveRegion = document.querySelector<HTMLElement>("#copy-status");
  try {
    await navigator.clipboard.writeText(command);
    button.classList.add("command--copied");
    button.setAttribute("aria-label", `已复制：${command}`);
    if (liveRegion) liveRegion.textContent = "拉取命令已复制到剪贴板。";
    window.setTimeout(() => {
      button.classList.remove("command--copied");
      button.setAttribute("aria-label", originalLabel);
    }, 1600);
  } catch {
    if (liveRegion) liveRegion.textContent = "复制失败，请手动选择命令。";
  }
}

function toggleCard(button: HTMLButtonElement): void {
  const tag = button.dataset.cardToggle;
  const card = button.closest<HTMLElement>("[data-image-card]");
  const detailsId = button.getAttribute("aria-controls");
  const details = detailsId ? document.getElementById(detailsId) : null;
  if (!tag || !card || !details) throw new Error("镜像卡片交互结构不完整。");
  const isExpanded = !expandedCards.has(tag);
  if (isExpanded) expandedCards.add(tag);
  else expandedCards.delete(tag);
  card.classList.toggle("image-card--expanded", isExpanded);
  details.setAttribute("aria-hidden", String(!isExpanded));
  details.toggleAttribute("inert", !isExpanded);
  button.setAttribute("aria-expanded", String(isExpanded));
  button.setAttribute("aria-label", `${isExpanded ? "收起" : "展开"} ${tag} 镜像详情`);
}

function bindDetailInteractions(): void {
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;
    const copy = event.target.closest<HTMLButtonElement>("[data-copy]");
    if (copy) {
      void copyCommand(copy);
      return;
    }
    const toggle = event.target.closest<HTMLButtonElement>("[data-card-toggle]");
    if (toggle) toggleCard(toggle);
  });
  document.addEventListener("keydown", (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.id === "search" && event.key === "Escape") {
      clearSearch(target);
      return;
    }
    if (
      event.key !== "/" ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }
    event.preventDefault();
    document.querySelector<HTMLInputElement>("#search")?.focus();
  });
}

function renderResourceDetail(resourceCatalog: ResourceCatalog): void {
  catalog = resourceCatalog;
  query = "";
  versionFilter = "";
  sort = "version-desc";
  visibleCount = PAGE_SIZE;
  expandedCards.clear();
  const latestStable = getLatestStable(catalog.images);
  if (!latestStable) throw new TypeError("最新稳定版不在镜像目录中。");
  const majors = getMajorVersions(catalog.images);
  document.title = `${catalog.resource.name} · 极狐GitLab 镜像资源`;
  const shell = document.querySelector<HTMLElement>(".site-shell");
  const main = document.querySelector<HTMLElement>("#main-content");
  if (!shell || !main) throw new Error("页面骨架不完整。");
  shell.className = "site-shell resource-detail-page";
  main.className = "";
  main.innerHTML = `
        <nav class="breadcrumb content-width" aria-label="面包屑">
          <a href="/" data-route>资源列表</a>
          <img src="/assets/slash.svg" alt="" />
          <span aria-current="page">${escapeHtml(catalog.resource.name)}</span>
        </nav>
        <section class="resource-hero content-width" aria-labelledby="resource-title">
          <div class="resource-hero__identity">
            <h1 id="resource-title">${escapeHtml(catalog.resource.name)}</h1>
            <p>${escapeHtml(catalog.resource.description)}</p>
          </div>
          <div class="latest-panel">
            <div class="latest-panel__row">
              <div class="latest-panel__meta">
                <span class="latest-panel__label">最新稳定版</span>
                <strong>${escapeHtml(latestStable.canonicalTag)}</strong>
                <span class="latest-panel__platforms">${archChips(latestStable, " arch-chip--hero")}</span>
              </div>
              <p>发布于：${escapeHtml(formatDate(latestStable.pushedAt))}</p>
            </div>
            ${commandButton(latestStable.pullCommand, "复制最新稳定版拉取命令")}
          </div>
        </section>

        <section id="catalog" class="catalog-section content-width" aria-labelledby="catalog-title">
          <div class="catalog-heading">
            <h2 id="catalog-title">镜像版本</h2>
            <div class="filters">
              <div class="search-field">
                <label class="sr-only" for="search">搜索版本、架构或摘要</label>
                <img src="/assets/search.svg" alt="" />
                <input id="search" type="search" placeholder="共收录 ${catalog.stats.totalImages} 个镜像版本" autocomplete="off" aria-describedby="result-count" />
                <button id="search-clear" class="search-clear" type="button" aria-label="清除搜索" hidden><span aria-hidden="true">×</span></button>
              </div>
              <label class="select-field select-field--version">
                <span class="sr-only">版本筛选</span>
                <select id="version-filter">
                  <option value="">全部版本</option>
                  ${catalog.resource.channels
                    .map(
                      (channel) =>
                        `<option value="${escapeHtml(channel.id)}">${escapeHtml(channel.label)}</option>`,
                    )
                    .join("")}
                  ${majors.map((value) => `<option value="${value}">${value}</option>`).join("")}
                </select>
              </label>
              <label class="select-field select-field--sort">
                <span class="sr-only">排序</span>
                <select id="sort">
                  <option value="version-desc">排序: 版本号倒序</option>
                  <option value="version-asc">排序: 版本号正序</option>
                  <option value="date-desc">排序: 时间由新到旧</option>
                  <option value="date-asc">排序: 时间由旧到新</option>
                </select>
              </label>
            </div>
          </div>
          <p id="result-count" class="sr-only" aria-live="polite"></p>
          <div id="image-list" class="image-list"></div>
          <div class="load-more"><button id="load-more" type="button">加载更多</button></div>
        </section>`;

  const searchInput = document.querySelector<HTMLInputElement>("#search");
  const versionSelect = document.querySelector<HTMLSelectElement>("#version-filter");
  const sortSelect = document.querySelector<HTMLSelectElement>("#sort");
  searchInput?.addEventListener("input", () => {
    query = searchInput.value;
    visibleCount = PAGE_SIZE;
    updateSearchClearButton(searchInput);
    renderResults();
  });
  document.querySelector<HTMLButtonElement>("#search-clear")?.addEventListener("click", () => {
    if (!searchInput) throw new Error("搜索框不存在。");
    clearSearch(searchInput);
    searchInput.focus();
  });
  versionSelect?.addEventListener("change", () => {
    versionFilter = versionSelect.value;
    visibleCount = PAGE_SIZE;
    renderResults();
  });
  sortSelect?.addEventListener("change", () => {
    sort = sortSelect.value as SortOption;
    visibleCount = PAGE_SIZE;
    renderResults();
  });
  document.querySelector<HTMLButtonElement>("#load-more")?.addEventListener("click", () => {
    visibleCount += PAGE_SIZE;
    renderResults();
  });
  renderResults();
}

function currentSlug(pathname = window.location.pathname): string | null {
  const path = pathname.replace(/\/+$/, "");
  if (path === "") return null;
  const segments = path.split("/").filter(Boolean);
  return segments.length === 1 ? decodeURIComponent(segments[0]) : "__not_found__";
}

function renderFatalError(error: unknown): void {
  const message = error instanceof Error ? error.message : "未知错误";
  const shell = document.querySelector<HTMLElement>(".site-shell");
  const main = document.querySelector<HTMLElement>("#main-content");
  if (!shell || !main) {
    renderSiteShell();
    renderFatalError(error);
    return;
  }
  shell.className = "site-shell";
  main.className = "fatal-error";
  main.innerHTML = `
      <div>
        <h1>暂时无法载入镜像目录</h1>
        <p>${escapeHtml(message)}</p>
        <a href="/" data-route>返回资源列表</a>
      </div>`;
}

async function renderRoute(index: ResourceIndex, pathname: string): Promise<void> {
  const version = ++routeVersion;
  try {
    const slug = currentSlug(pathname);
    if (slug === null) {
      renderResourceIndex(index);
      window.scrollTo(0, 0);
      return;
    }
    const resource = index.resources.find((candidate) => candidate.slug === slug);
    if (!resource) throw new Error("未找到对应的镜像资源。");
    const resourceCatalog = validateResourceCatalog(
      await fetchJson(resource.dataPath, `${resource.name} 镜像数据`),
    );
    if (version !== routeVersion) return;
    if (
      resourceCatalog.resource.id !== resource.id ||
      resourceCatalog.resource.slug !== resource.slug ||
      resourceCatalog.resource.repository !== resource.repository
    ) {
      throw new TypeError("资源详情与资源索引不一致。");
    }
    renderResourceDetail(resourceCatalog);
    window.scrollTo(0, 0);
  } catch (error) {
    if (version !== routeVersion) return;
    renderFatalError(error);
  }
}

function bindRouteInteractions(index: ResourceIndex): void {
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || !(event.target instanceof Element)) return;
    const mouseEvent = event as MouseEvent;
    if (
      mouseEvent.button !== 0 ||
      mouseEvent.metaKey ||
      mouseEvent.ctrlKey ||
      mouseEvent.shiftKey ||
      mouseEvent.altKey
    ) {
      return;
    }
    const link = event.target.closest<HTMLAnchorElement>("a[data-route]");
    if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin) return;
    event.preventDefault();
    window.history.pushState(
      null,
      "",
      `${destination.pathname}${destination.search}${destination.hash}`,
    );
    void renderRoute(index, destination.pathname);
  });
  window.addEventListener("popstate", () => {
    void renderRoute(index, window.location.pathname);
  });
}

async function bootstrap(): Promise<void> {
  renderSiteShell();
  bindDetailInteractions();
  window.addEventListener("resize", syncResourceTitleTooltips, { passive: true });
  try {
    const index = validateResourceIndex(await fetchJson("/data/resources.json", "资源索引"));
    bindRouteInteractions(index);
    await renderRoute(index, window.location.pathname);
  } catch (error) {
    renderFatalError(error);
  }
}

void bootstrap();
